import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import HabitCard from './HabitCard';

// Wrapper component for sortable items
const SortableHabitItem = ({ habit, onDelete, onEdit, onToggle, onAddNote }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: habit.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group/drag">
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 opacity-0 group-hover/drag:opacity-100 cursor-grab active:cursor-grabbing p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-opacity z-10"
      >
        <GripVertical className="w-4 h-4 text-slate-400" />
      </div>
      
      <HabitCard
        habit={habit}
        onDelete={onDelete}
        onEdit={onEdit}
        onToggle={onToggle}
        onAddNote={onAddNote}
      />
    </div>
  );
};

const DraggableHabitList = ({ habits, onDelete, onEdit, onToggle, onAddNote, onReorder }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = habits.findIndex((h) => h.id === active.id);
      const newIndex = habits.findIndex((h) => h.id === over.id);
      
      const newHabits = arrayMove(habits, oldIndex, newIndex);
      
      // Call the reorder callback with new order
      if (onReorder) {
        onReorder(newHabits);
      }
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={habits.map((h) => h.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="grid gap-3 pl-6" data-testid="habits-list">
          {habits.map((habit) => (
            <SortableHabitItem
              key={habit.id}
              habit={habit}
              onDelete={onDelete}
              onEdit={onEdit}
              onToggle={onToggle}
              onAddNote={onAddNote}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default DraggableHabitList;
